import { AxiosResponse } from "axios";

import { Base } from "@tt-app/api/base";
import {
  RespExerciseStart,
  RespExerciseEnd,
  PayloadExerciseEnd,
  PayloadExerciseInfo,
  PayloadExerciseStart,
  PayloadExerciseConversationAnswer,
  RespExerciseConversationAnswer,
  ChatBasedExercise,
  PayloadExerciseBase,
  RespDrillEnd,
  PayloadDrillTranslationAnswer,
  RespDrillTranslationAnswer,
  PayloadExercisePracticeStart
} from "@tt-app/models/exercise/";
import { buildAuthHeaders, logger } from "@tt-app/utils";
import { API_ENDPOINTS, LOGGER_EVENTS } from "@tt-app/constants";
import {
  RespExerciseDictationAnswer,
  PayloadExerciseDictationAnswer
} from "@tt-app/models/exercise/dictation";
import {
  DrillBasedExercise,
  PayloadDrillPronunciationAnswer,
  RespDrillPronunciationAnswer,
  RespDrillListeningAnswer,
  PayloadDrillListeningAnswer,
  PayloadDrillEnd,
  PayloadDrillSkippedAnswer,
  RespDrillAnswer,
  PayloadDrillStart,
  RespDrillVocabularyAnswer,
  PayloadDrillVocabularyAnswer
} from "@tt-app/models/exercise/drill";

const { EXERCISE } = API_ENDPOINTS;

interface PayloadExerciseQuizTranslation {
  aliasUuid: string;
  courseUuid: string;
  exerciseType: number;
  exerciseUuid: string;
  token: string;
}

class Exercise extends Base {
  /**
   * New Structure
   */
  async fetchExercise(payload: PayloadExerciseQuizTranslation): Promise<AxiosResponse> {
    const { courseUuid, exerciseType, exerciseUuid } = payload;

    const url = `lingos-exercises/exercises/${courseUuid}/${exerciseType}/${exerciseUuid}/`;

    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    return this.httpClient.get(url, { headers });
  }

  async logExerciseEnd(payload: any): Promise<AxiosResponse> {
    const { courseUuid, exerciseType, exerciseUuid } = payload;

    const url = `lingos-exercises/exercises/${courseUuid}/${exerciseType}/${exerciseUuid}/end/`;

    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    const postParams = {
      logId: payload.logId,
      numCorrectAnswers: payload.numCorrectAnswers,
      numIncorrectAnswers: payload.numIncorrectAnswers
    };

    return this.httpClient.post(url, postParams, { headers });
  }

  async logExerciseStart(payload: any): Promise<AxiosResponse> {
    const { courseUuid, exerciseType, exerciseUuid } = payload;
    let body: { [key: string]: string | string[] | number } = payload.assignmentId
      ? { assignmentId: payload.assignmentId }
      : {};

    if (payload.sessionId && payload.limitedModeId) {
      body = {
        ...body,
        limitedModeId: payload.limitedModeId,
        sessionId: payload.sessionId
      };
    }

    const url = `lingos-exercises/exercises/${courseUuid}/${exerciseType}/${exerciseUuid}/start/`;

    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    return this.httpClient.post(url, body, { headers });
  }

  async logExerciseAnswer(payload: any): Promise<AxiosResponse> {
    const { answer, courseUuid, exerciseType, exerciseUuid, logId, problemUuid, skipped } = payload;

    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    const postParams = {
      answer,
      logId,
      problemUuid,
      skipped
    };

    const url = `lingos-exercises/exercises/${courseUuid}/${exerciseType}/${exerciseUuid}/log/`;

    return this.httpClient.post(url, postParams, { headers });
  }

  async logTranslationQuizAnswers(payload: any): Promise<AxiosResponse> {
    const { answer, courseUuid, exerciseType, exerciseUuid, logId, problemUuid } = payload;

    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    const postParams = {
      answer,
      logId,
      problemUuid
    };

    const url = `lingos-exercises/exercises/${courseUuid}/${exerciseType}/${exerciseUuid}/log/`;

    return this.httpClient.post(url, postParams, { headers });
  }

  /**
   * Old structure
   */
  async getExercise<T>(payload: PayloadExerciseInfo): Promise<T> {
    const url = `${EXERCISE.FETCH(payload)}?${this.buildLangQueryParam()}`;
    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };

    try {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_DETAILS_REQUEST.SUCCESS, {
        ...payload,
        url
      });

      const resp: AxiosResponse<T> = await this.httpClient.get(url, { headers });

      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_DETAILS_RESPONSE.SUCCESS, {
        ...payload,
        url
      });

      return resp.data;
    } catch (error) {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_DETAILS_RESPONSE.FAILURE, {
        ...payload,
        error,
        url
      });

      return error;
    }
  }

  async getPractice<T>(payload: PayloadExerciseInfo): Promise<T> {
    const url = `/lingos-exercises/personalized-exercises/${payload.exerciseType}/?numCards=${payload.problemCount}&schoolGrade=${payload.schoolGrade}`;
    const headers = { ...this.buildAuthHeaders(payload), ...this.buildLangHeader() };
    const resp: AxiosResponse<T> = await this.httpClient.get(url, { headers });

    return resp.data;
  }

  /**
   * Exercise based on chat (conversation, dictation...) are relying on the same endpoint
   * structure, the difference being the exercise type in the URL
   *
   * @param payload
   */
  async getChatBasedExercise(payload: PayloadExerciseInfo): Promise<ChatBasedExercise> {
    return this.getExercise<ChatBasedExercise>(payload);
  }

  /**
   * While the endpoint URL pattern is the same as ChatBased exercises, the returned type
   * is different
   *
   * @param payload
   */
  async getDrillBasedExercise(payload: PayloadExerciseInfo): Promise<DrillBasedExercise> {
    return this.getExercise<DrillBasedExercise>(payload);
  }

  async getDrillBasedPractice(payload: PayloadExerciseInfo): Promise<DrillBasedExercise> {
    return this.getPractice<DrillBasedExercise>(payload);
  }

  async getQuizBasedExercise(payload: any): Promise<{}> {
    return this.getExercise(payload);
  }

  /**
   * Starting any exercise type is returning the logId
   *
   * @param payload
   * @returns start response with logId
   */
  async startExercise(payload: PayloadExerciseStart): Promise<RespExerciseStart> {
    // --- Assignment specific
    let body: { [key: string]: string | string[] | number } = payload.assignmentId
      ? { assignmentId: payload.assignmentId }
      : {};

    // --- Drill specific
    const drillPayload = payload as PayloadDrillStart;
    if (drillPayload.cardSetUuid) {
      body.cardSetUuid = drillPayload.cardSetUuid;
    }

    if (payload.problemUuids) {
      body.problemUuids = payload.problemUuids;
    }

    if (payload.sessionId && payload.limitedModeId) {
      body = {
        ...body,
        limitedModeId: payload.limitedModeId,
        sessionId: payload.sessionId
      };
    }

    const url = EXERCISE.START(payload);

    try {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_START_REQUEST.SUCCESS, {
        ...payload,
        url
      });

      const resp: AxiosResponse<RespExerciseStart> = await this.httpClient.post(url, body, {
        headers: buildAuthHeaders(payload)
      });

      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_START_RESPONSE.SUCCESS, {
        ...payload,
        ...resp.data,
        url
      });

      return resp.data;
    } catch (error) {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_START_RESPONSE.FAILURE, {
        ...payload,
        error,
        url
      });

      return error;
    }
  }

  async startExercisePractice(payload: PayloadExercisePracticeStart): Promise<RespExerciseStart> {
    // --- Assignment specific
    const body: { [key: string]: undefined | string | string[] | number } = {};

    if (payload.problemUuids) {
      body.problemUuids = payload.problemUuids;
      body.logId = payload.logId;
    }

    const resp: AxiosResponse<RespExerciseStart> = await this.httpClient.post(
      EXERCISE.START_EXERCISE_PRACTICE(payload),
      body,
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }

  async startPractice(payload: PayloadExerciseStart): Promise<RespExerciseStart> {
    // --- Assignment specific
    const body: { [key: string]: string | string[] | number } = payload.assignmentId
      ? { assignmentId: payload.assignmentId }
      : {};

    // --- Drill specific
    const drillPayload = payload as PayloadDrillStart;
    if (drillPayload.cardSetUuid) {
      body.cardSetUuid = drillPayload.cardSetUuid;
    }

    if (payload.problemUuids) {
      body.problemUuids = payload.problemUuids;
    }

    if (payload.schoolGrade) {
      body.schoolGrade = payload.schoolGrade;
    }

    const resp: AxiosResponse<RespExerciseStart> = await this.httpClient.post(
      `/lingos-exercises/personalized-exercises/${payload.exerciseType}/start/`,
      body,
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }

  async submitAnswer<Req extends PayloadExerciseBase, Resp>(payload: Req): Promise<Resp> {
    const url = EXERCISE.SUBMIT_ANSWER(payload);

    try {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_LOG_REQUEST.SUCCESS, { ...payload, url });

      const resp: AxiosResponse<Resp> = await this.httpClient.post(url, payload, {
        headers: buildAuthHeaders(payload)
      });

      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_LOG_RESPONSE.SUCCESS, {
        ...payload,
        ...resp.data,
        url
      });

      return resp.data;
    } catch (error) {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_LOG_RESPONSE.FAILURE, {
        ...payload,
        error,
        url
      });

      return error;
    }
  }

  async submitPracticeAnswer<Req extends PayloadExerciseBase, Resp>(payload: Req): Promise<Resp> {
    const resp: AxiosResponse<Resp> = await this.httpClient.post(
      `/lingos-exercises/personalized-exercises/${payload.exerciseType}/log/`,
      payload,
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }

  async submitConversationAnswer(
    payload: PayloadExerciseConversationAnswer
  ): Promise<RespExerciseConversationAnswer> {
    return this.submitAnswer<PayloadExerciseConversationAnswer, RespExerciseConversationAnswer>(
      payload
    );
  }

  async submitDictationAnswer(
    payload: PayloadExerciseDictationAnswer
  ): Promise<RespExerciseDictationAnswer> {
    return this.submitAnswer<PayloadExerciseDictationAnswer, RespExerciseDictationAnswer>(payload);
  }

  async submitDrillSkippedAnswer(payload: PayloadDrillSkippedAnswer): Promise<RespDrillAnswer> {
    return this.submitAnswer<PayloadDrillSkippedAnswer, RespDrillAnswer>(payload);
  }

  async submitDrillPracticeSkippedAnswer(
    payload: PayloadDrillSkippedAnswer
  ): Promise<RespDrillAnswer> {
    return this.submitPracticeAnswer<PayloadDrillSkippedAnswer, RespDrillAnswer>(payload);
  }

  async submitDrillPronunciationAnswer(
    payload: PayloadDrillPronunciationAnswer
  ): Promise<RespDrillPronunciationAnswer> {
    return this.submitAnswer<PayloadDrillPronunciationAnswer, RespDrillPronunciationAnswer>(
      payload
    );
  }

  async submitDrillPronunciationPracticeAnswer(
    payload: PayloadDrillPronunciationAnswer
  ): Promise<RespDrillPronunciationAnswer> {
    return this.submitPracticeAnswer<PayloadDrillPronunciationAnswer, RespDrillPronunciationAnswer>(
      payload
    );
  }

  async submitDrillListeningAnswer(
    payload: PayloadDrillListeningAnswer
  ): Promise<RespDrillListeningAnswer> {
    return this.submitAnswer<PayloadDrillListeningAnswer, RespDrillListeningAnswer>(payload);
  }

  async submitDrillListeningPracticeAnswer(
    payload: PayloadDrillListeningAnswer
  ): Promise<RespDrillListeningAnswer> {
    return this.submitPracticeAnswer<PayloadDrillListeningAnswer, RespDrillListeningAnswer>(
      payload
    );
  }

  async submitDrillVocabularyAnswer(
    payload: PayloadDrillVocabularyAnswer
  ): Promise<RespDrillVocabularyAnswer> {
    return this.submitAnswer<PayloadDrillVocabularyAnswer, RespDrillVocabularyAnswer>(payload);
  }

  async submitDrillTranslationAnswer(
    payload: PayloadDrillTranslationAnswer
  ): Promise<RespDrillTranslationAnswer> {
    return this.submitAnswer<PayloadDrillTranslationAnswer, RespDrillTranslationAnswer>(payload);
  }

  async submitDrillVocabularyPracticeAnswer(
    payload: PayloadDrillVocabularyAnswer
  ): Promise<RespDrillVocabularyAnswer> {
    return this.submitPracticeAnswer<PayloadDrillVocabularyAnswer, RespDrillVocabularyAnswer>(
      payload
    );
  }

  /**
   * End exercise structure is assumed to be shared by all exercise type
   * @param payload
   */
  async endSingleSentenceReadingExercise(payload: any): Promise<any> {
    const resp: AxiosResponse<RespExerciseEnd> = await this.httpClient.post(
      EXERCISE.END(payload),
      {
        logId: payload.logId,
        totalScore: payload.totalScore
      },
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }

  async submitSingleSentenceReadingPronunciationPractice(payload: any) {
    await this.httpClient.post(EXERCISE.SUBMIT_ANSWER(payload), payload, {
      headers: buildAuthHeaders(payload),
      params: {
        wordPronunciationPractice: 1
      }
    });
  }
  /**
   * End exercise structure is assumed to be shared by all exercise type
   * @param payload
   */
  async endExercise(payload: PayloadExerciseEnd): Promise<RespExerciseEnd> {
    const resp: AxiosResponse<RespExerciseEnd> = await this.httpClient.post(
      EXERCISE.END(payload),
      {
        logId: payload.logId,
        numCorrectAnswers: payload.numCorrectAnswers,
        numIncorrectAnswers: payload.numIncorrectAnswers,
        numSkipped: payload.numSkipped
      },
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }

  /**
   * As-of Aug-2020, the payload for ending a drill exercise is the same as chat
   * exercises
   *
   * @param payload
   */
  async endExerciseDrill(payload: PayloadDrillEnd): Promise<RespDrillEnd> {
    const url = EXERCISE.END(payload);

    try {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_END_REQUEST.SUCCESS, { ...payload, url });

      const resp: AxiosResponse<RespDrillEnd> = await this.httpClient.post(
        url,
        {
          logId: payload.logId,
          numCorrectAnswers: payload.numCorrectAnswers,
          numIncorrectAnswers: payload.numIncorrectAnswers,
          numSkipped: payload.numSkipped
        },
        { headers: buildAuthHeaders(payload) }
      );

      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_END_RESPONSE.SUCCESS, { ...payload, url });

      return resp.data;
    } catch (error) {
      logger.logEvent(LOGGER_EVENTS.APPLICATION.EXERCISE_END_RESPONSE.FAILURE, {
        ...payload,
        error,
        url
      });

      return error;
    }
  }

  async endPracticeDrill(payload: PayloadDrillEnd): Promise<RespDrillEnd> {
    const resp: AxiosResponse<RespDrillEnd> = await this.httpClient.post(
      `/lingos-exercises/personalized-exercises/${payload.exerciseType}/end/`,
      {
        logId: payload.logId,
        numCorrectAnswers: payload.numCorrectAnswers,
        numIncorrectAnswers: payload.numIncorrectAnswers,
        numSkipped: payload.numSkipped
      },
      { headers: buildAuthHeaders(payload) }
    );

    return resp.data;
  }
}

export { Exercise };
